import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Link2, MousePointerClick, Mail, Globe, DollarSign, ArrowRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const steps = [
    { icon: Mail, label: "Email Blast", desc: "Subscriber clicks tracked CTA" },
    { icon: Globe, label: "Website Visit", desc: "Landing on unifiedlendinghub.com / uscreditnexus.com" },
    { icon: MousePointerClick, label: "Offer Click", desc: "Redirect with unique click ID" },
    { icon: DollarSign, label: "Conversion", desc: "S2S postback from network" },
  ];

  return (
    <div className="min-h-screen flex bg-slate-50">
      <div className="hidden lg:flex flex-col justify-center w-1/2 bg-slate-900 text-white p-14">
        <div className="flex items-center gap-3 mb-10">
          <div className="h-10 w-10 rounded-xl bg-indigo-600 flex items-center justify-center">
            <Link2 className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg tracking-tight">Omnilink Solutions</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">Affiliate Tracking Portal</div>
          </div>
        </div>
        <h1 className="font-heading text-3xl xl:text-4xl font-bold tracking-tight leading-tight mb-4">
          Track every customer from<br />email to conversion.
        </h1>
        <p className="text-slate-400 text-sm mb-10 max-w-md">
          Full-funnel attribution across your email platform, both websites, and every affiliate network offer — with S2S postback precision.
        </p>
        <div className="space-y-3 max-w-md">
          {steps.map((s, i) => (
            <div key={s.label} className="flex items-center gap-4 bg-slate-800/60 border border-slate-700/60 rounded-lg px-4 py-3">
              <div className="h-9 w-9 rounded-lg bg-indigo-600/20 text-indigo-400 flex items-center justify-center shrink-0">
                <s.icon className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold">{i + 1}. {s.label}</div>
                <div className="text-xs text-slate-400">{s.desc}</div>
              </div>
              {i < 3 && <ArrowRight className="h-4 w-4 text-slate-600" />}
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Link2 className="h-4 w-4 text-white" />
            </div>
            <span className="font-heading font-bold text-slate-900">Omnilink Solutions</span>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 mb-1">Welcome back</h2>
          <p className="text-sm text-slate-500 mb-8">Sign in to access your tracking dashboard.</p>
          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-button"
            className="w-full flex items-center justify-center gap-3 bg-white border border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-800 font-medium text-sm rounded-lg px-4 py-3 transition-colors shadow-sm"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
            </svg>
            Sign in with Google
          </button>
          <p className="text-xs text-slate-400 mt-6 text-center">
            Secured with Google OAuth · Sessions last 7 days
          </p>
        </div>
      </div>
    </div>
  );
}
